import React, { useState, useRef, useEffect, useCallback } from "react";
import {
  X,
  Maximize2,
  ZoomIn,
  ZoomOut,
  Brush,
  Wand2,
  ScanEye,
  Save,
  RotateCcw,
  Loader2,
  Code,
  Layers,
  Download,
  Hand,
  Palette,
  Square,
  Circle,
  Eraser,
  Undo,
  Redo,
} from "lucide-react";
import { authenticatedFetch } from "../utils/api";
import { API_HOST } from "../utils/constants";
import { extractTaskResultUrl, submitTask, startTaskPolling } from "../services/taskService";
import { useTheme } from "../context/ThemeContext";
import { useToast } from "../context/ToastContext";
import WanxEditPanel from "./WanxEditPanel";
import ResourceSelector from "./resource/ResourceSelector";
import {
  getCurrentImageProvider,
  isWanxImageProvider,
} from "../utils/wanxImageCapabilities";

const WANX_IMAGE_MODEL = "qwen-image-3.0-pro";
const WANX_MAX_EXTRA_REFERENCE_IMAGES = 2;
const WANX_OUTPUT_AREA = {
  "1K": 1024 * 1024,
  "2K": 2048 * 2048,
};
const WANX_MAX_OUTPUT_AREA = 2048 * 2048;

const loadImageNaturalSize = (src) => new Promise((resolve, reject) => {
  const image = new Image();
  image.onload = () => resolve({
    width: image.naturalWidth || image.width,
    height: image.naturalHeight || image.height,
  });
  image.onerror = () => reject(new Error("无法读取当前图片尺寸，请重新加载图片后再试"));
  image.src = src;
});

const toWanxOutputSize = (width, height, sizeTier) => {
  const naturalWidth = Number(width);
  const naturalHeight = Number(height);
  const targetArea = WANX_OUTPUT_AREA[sizeTier];
  if (!targetArea || !Number.isFinite(naturalWidth) || !Number.isFinite(naturalHeight)
    || naturalWidth <= 0 || naturalHeight <= 0) {
    throw new Error("无法根据当前图片计算输出尺寸");
  }

  const aspectRatio = naturalWidth / naturalHeight;
  if (aspectRatio < 1 / 8 || aspectRatio > 8) {
    throw new Error("当前图片宽高比超出千问图像3.0支持的 1:8 至 8:1 范围");
  }

  const roundToEight = (value) => Math.max(8, Math.round(value / 8) * 8);
  let outputWidth = roundToEight(Math.sqrt(targetArea * aspectRatio));
  let outputHeight = roundToEight(Math.sqrt(targetArea / aspectRatio));

  // 2K 档四舍五入后可能略超接口允许的最大像素面积，向下收敛到合法范围。
  if (outputWidth * outputHeight > WANX_MAX_OUTPUT_AREA) {
    const scale = Math.sqrt(WANX_MAX_OUTPUT_AREA / (outputWidth * outputHeight));
    outputWidth = Math.max(8, Math.floor((outputWidth * scale) / 8) * 8);
    outputHeight = Math.max(8, Math.floor((outputHeight * scale) / 8) * 8);
  }

  // 边界比例在整数取整后可能略微越过 1:8 / 8:1，向内收敛确保接口合法。
  if (outputWidth / outputHeight > 8) {
    outputWidth = outputHeight * 8;
  } else if (outputHeight / outputWidth > 8) {
    outputHeight = outputWidth * 8;
  }

  return `${outputWidth}*${outputHeight}`;
};

const ImageWorkstation = ({
  imageUrl,
  onClose,
  onSave,
  leftOffset = "0px",
  projectId,
  episodeId,
}) => {
  const { getTheme } = useTheme();
  const theme = getTheme();
  const toast = useToast();
  const [currentImage, setCurrentImage] = useState(imageUrl);
  const [mode, setMode] = useState("view"); // view | mask | wanx
  const [drawTool, setDrawTool] = useState("brush"); // brush | rectangle | circle | eraser
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  // 图片历史栈（用于撤回）
  const [imageHistory, setImageHistory] = useState([]);
  const [imageHistoryIndex, setImageHistoryIndex] = useState(-1);

  // 万相编辑 state
  const [wanxFeature, setWanxFeature] = useState("edit"); // edit | multiref
  const [wanxPrompt, setWanxPrompt] = useState("");
  const [wanxRefImages, setWanxRefImages] = useState([]); // [{url, name}]
  const [wanxSize, setWanxSize] = useState("auto");
  const [showResourceSelector, setShowResourceSelector] = useState(false);
  const [wanxProcessing, setWanxProcessing] = useState(false);

  const notifyWanxReferenceLimit = useCallback(() => {
    toast.warning("千问图像3.0最多支持3张输入图；当前图已占1张，最多再添加2张参考图");
  }, [toast]);

  const addWanxReferenceImage = useCallback((image) => {
    if (!image?.url) return false;
    if (wanxRefImages.length >= WANX_MAX_EXTRA_REFERENCE_IMAGES) {
      notifyWanxReferenceLimit();
      return false;
    }
    setWanxRefImages((previous) => {
      if (previous.length >= WANX_MAX_EXTRA_REFERENCE_IMAGES) return previous;
      return [...previous, image];
    });
    return true;
  }, [notifyWanxReferenceLimit, wanxRefImages.length]);

  const openWanxResourceSelector = useCallback(() => {
    if (wanxRefImages.length >= WANX_MAX_EXTRA_REFERENCE_IMAGES) {
      notifyWanxReferenceLimit();
      return;
    }
    setShowResourceSelector(true);
  }, [notifyWanxReferenceLimit, wanxRefImages.length]);

  // 同步外部 imageUrl prop 变化
  useEffect(() => {
    setCurrentImage(imageUrl);
    setImageHistory([imageUrl]);
    setImageHistoryIndex(0);
  }, [imageUrl]);

  // 切换图片（带历史记录）
  const pushImage = (newUrl) => {
    setImageHistory(prev => {
      const trimmed = prev.slice(0, imageHistoryIndex + 1);
      const next = [...trimmed, newUrl];
      if (next.length > 20) next.shift();
      setImageHistoryIndex(next.length - 1);
      return next;
    });
    setCurrentImage(newUrl);
  };

  const canUndoImage = imageHistoryIndex > 0;
  const canRedoImage = imageHistoryIndex < imageHistory.length - 1;

  const undoImage = () => {
    if (!canUndoImage) return;
    const newIdx = imageHistoryIndex - 1;
    setImageHistoryIndex(newIdx);
    setCurrentImage(imageHistory[newIdx]);
  };

  const redoImage = () => {
    if (!canRedoImage) return;
    const newIdx = imageHistoryIndex + 1;
    setImageHistoryIndex(newIdx);
    setCurrentImage(imageHistory[newIdx]);
  };

  // 状态Ref化：避免闭包陷阱
  const stateRef = useRef({
    isDragging: false,
    isDrawing: false,
    dragStart: { x: 0, y: 0 },
    drawStart: { x: 0, y: 0 },
    scale: 1,
    offset: { x: 0, y: 0 },
    mode: "view",
    drawTool: "brush",
    isSpacePressed: false,
    imgW: 0,
    imgH: 0,
  });

  useEffect(() => {
    stateRef.current.scale = scale;
    stateRef.current.offset = offset;
    stateRef.current.mode = mode;
    stateRef.current.drawTool = drawTool;
  }, [scale, offset, mode, drawTool]);

  const viewportRef = useRef(null);
  const canvasRef = useRef(null);
  const maskCanvasRef = useRef(null);
  const tempCanvasRef = useRef(null); // 用于矩形/圆形绘制时的临时保存
  const imgRef = useRef(null);

  // UI 状态
  const [brushSize, setBrushSize] = useState(20);
  const [brushOpacity, setBrushOpacity] = useState(0.6);
  const [maskColor, setMaskColor] = useState("#ff0000"); // canvas 遮罩绘制色，与主题无关，保留
  const [hasMask, setHasMask] = useState(false);
  const [isSpacePressed, setIsSpacePressed] = useState(false);

  // 历史记录
  const [history, setHistory] = useState([]);
  const [historyIndex, setHistoryIndex] = useState(-1);

  const [prompt, setPrompt] = useState("");
  const [isProcessing, setIsProcessing] = useState(false);
  const [analysisResult, setAnalysisResult] = useState(null);
  const [showJsonPanel, setShowJsonPanel] = useState(false);

  // 初始化逻辑 Canvas (隐藏的纯白 Canvas)
  useEffect(() => {
    if (!maskCanvasRef.current) {
      maskCanvasRef.current = document.createElement("canvas");
    }
  }, []);

  // ✅ 核心修复：完全依赖 DOM 图片加载来设置尺寸
  const handleImageLoad = (e) => {
    const { naturalWidth, naturalHeight } = e.target;

    // 1. 更新 Ref 尺寸，用于拖拽边界计算
    stateRef.current.imgW = naturalWidth;
    stateRef.current.imgH = naturalHeight;

    // 2. 强制设置显示层 Canvas 的内部分辨率
    if (canvasRef.current) {
      canvasRef.current.width = naturalWidth;
      canvasRef.current.height = naturalHeight;
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, naturalWidth, naturalHeight);
    }

    // 3. 强制设置逻辑层 Canvas 的内部分辨率
    if (!maskCanvasRef.current) {
      maskCanvasRef.current = document.createElement("canvas");
    }
    if (maskCanvasRef.current) {
      maskCanvasRef.current.width = naturalWidth;
      maskCanvasRef.current.height = naturalHeight;
      const ctx = maskCanvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, naturalWidth, naturalHeight);
    }

    setHasMask(false);
    setHistory([]);
    setHistoryIndex(-1);
  };

  // 保存当前画布状态到历史记录
  const saveToHistory = () => {
    if (!canvasRef.current || !maskCanvasRef.current) return;

    const displayData = canvasRef.current.toDataURL();
    const maskData = maskCanvasRef.current.toDataURL();

    const newHistory = history.slice(0, historyIndex + 1);
    newHistory.push({ displayData, maskData });

    // 限制历史记录数量
    if (newHistory.length > 20) {
      newHistory.shift();
    } else {
      setHistoryIndex(historyIndex + 1);
    }

    setHistory(newHistory);
  };

  // 撤销
  const handleUndo = () => {
    if (historyIndex <= 0) return;

    const prevState = history[historyIndex - 1];
    restoreFromHistory(prevState);
    setHistoryIndex(historyIndex - 1);
  };

  // 重做
  const handleRedo = () => {
    if (historyIndex >= history.length - 1) return;

    const nextState = history[historyIndex + 1];
    restoreFromHistory(nextState);
    setHistoryIndex(historyIndex + 1);
  };

  // 从历史记录恢复画布
  const restoreFromHistory = (state) => {
    if (!canvasRef.current || !maskCanvasRef.current) return;

    const displayCtx = canvasRef.current.getContext("2d");
    const maskCtx = maskCanvasRef.current.getContext("2d");

    const displayImg = new Image();
    displayImg.onload = () => {
      displayCtx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      displayCtx.drawImage(displayImg, 0, 0);
    };
    displayImg.src = state.displayData;

    const maskImg = new Image();
    maskImg.onload = () => {
      maskCtx.clearRect(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
      maskCtx.drawImage(maskImg, 0, 0);

      // 检查是否有内容
      const imageData = maskCtx.getImageData(0, 0, maskCanvasRef.current.width, maskCanvasRef.current.height);
      const hasContent = imageData.data.some((value, index) => index % 4 === 3 && value > 0);
      setHasMask(hasContent);
    };
    maskImg.src = state.maskData;
  };

  // ================= 核心交互逻辑 =================

  const getCanvasPoint = (clientX, clientY) => {
    const canvas = canvasRef.current;
    if (!canvas) return { x: 0, y: 0 };

    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0 || rect.height === 0) return { x: 0, y: 0 };

    const scaleX = canvas.width / rect.width;
    const scaleY = canvas.height / rect.height;

    return {
      x: (clientX - rect.left) * scaleX,
      y: (clientY - rect.top) * scaleY,
    };
  };

  const drawLine = (x, y, isStart = false) => {
    if (!canvasRef.current || !maskCanvasRef.current) return;

    const tool = stateRef.current.drawTool;

    // 橡皮擦模式
    if (tool === "eraser") {
      const ctx = canvasRef.current.getContext("2d");
      ctx.globalCompositeOperation = "destination-out";
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = brushSize;
      if (isStart) {
        ctx.beginPath();
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      const maskCtx = maskCanvasRef.current.getContext("2d");
      maskCtx.globalCompositeOperation = "destination-out";
      maskCtx.lineCap = "round";
      maskCtx.lineJoin = "round";
      maskCtx.lineWidth = brushSize;
      if (isStart) {
        maskCtx.beginPath();
        maskCtx.moveTo(x, y);
      } else {
        maskCtx.lineTo(x, y);
        maskCtx.stroke();
      }
      return;
    }

    // 画笔模式
    if (tool === "brush") {
      // 显示层 (用户看到的颜色)
      const ctx = canvasRef.current.getContext("2d");
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.lineWidth = brushSize;
      ctx.strokeStyle = maskColor;
      ctx.globalAlpha = brushOpacity;
      ctx.globalCompositeOperation = "source-over";
      if (isStart) {
        ctx.beginPath();
        ctx.moveTo(x, y);
      } else {
        ctx.lineTo(x, y);
        ctx.stroke();
      }

      // 逻辑层 (纯白 Mask)
      const maskCtx = maskCanvasRef.current.getContext("2d");
      maskCtx.lineCap = "round";
      maskCtx.lineJoin = "round";
      maskCtx.lineWidth = brushSize;
      maskCtx.strokeStyle = "#FFFFFF";
      maskCtx.globalAlpha = 1.0;
      maskCtx.globalCompositeOperation = "source-over";
      if (isStart) {
        maskCtx.beginPath();
        maskCtx.moveTo(x, y);
      } else {
        maskCtx.lineTo(x, y);
        maskCtx.stroke();
      }

      setHasMask(true);
    }
  };

  // 绘制矩形
  const drawRectangle = (startX, startY, endX, endY) => {
    if (!canvasRef.current || !maskCanvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    const maskCtx = maskCanvasRef.current.getContext("2d");

    const width = endX - startX;
    const height = endY - startY;

    // 先恢复临时画布（如果有的话）
    if (tempCanvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(tempCanvasRef.current, 0, 0);
    }

    // 显示层
    ctx.strokeStyle = maskColor;
    ctx.lineWidth = brushSize;
    ctx.globalAlpha = brushOpacity;
    ctx.globalCompositeOperation = "source-over";
    ctx.strokeRect(startX, startY, width, height);

    // 逻辑层
    maskCtx.strokeStyle = "#FFFFFF";
    maskCtx.lineWidth = brushSize;
    maskCtx.globalAlpha = 1.0;
    maskCtx.globalCompositeOperation = "source-over";
    maskCtx.strokeRect(startX, startY, width, height);

    setHasMask(true);
  };

  // 绘制圆形
  const drawCircle = (centerX, centerY, radius) => {
    if (!canvasRef.current || !maskCanvasRef.current) return;

    const ctx = canvasRef.current.getContext("2d");
    const maskCtx = maskCanvasRef.current.getContext("2d");

    // 先恢复临时画布（如果有的话）
    if (tempCanvasRef.current) {
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
      ctx.drawImage(tempCanvasRef.current, 0, 0);
    }

    // 显示层
    ctx.strokeStyle = maskColor;
    ctx.lineWidth = brushSize;
    ctx.globalAlpha = brushOpacity;
    ctx.globalCompositeOperation = "source-over";
    ctx.beginPath();
    ctx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    ctx.stroke();

    // 逻辑层
    maskCtx.strokeStyle = "#FFFFFF";
    maskCtx.lineWidth = brushSize;
    maskCtx.globalAlpha = 1.0;
    maskCtx.globalCompositeOperation = "source-over";
    maskCtx.beginPath();
    maskCtx.arc(centerX, centerY, radius, 0, 2 * Math.PI);
    maskCtx.stroke();

    setHasMask(true);
  };

  const handleWindowMouseMove = useCallback(
    (e) => {
      const state = stateRef.current;

      if (state.isDragging) {
        // --- 平移 ---
        let newX = e.clientX - state.dragStart.x;
        let newY = e.clientY - state.dragStart.y;

        // 边界限制
        if (viewportRef.current && state.imgW > 0) {
          const viewportW = viewportRef.current.clientWidth;
          const viewportH = viewportRef.current.clientHeight;
          const scaledW = state.imgW * state.scale;
          const scaledH = state.imgH * state.scale;

          const limitX = Math.max(0, (scaledW - viewportW) / 2);
          const limitY = Math.max(0, (scaledH - viewportH) / 2);

          newX = Math.max(-limitX, Math.min(limitX, newX));
          newY = Math.max(-limitY, Math.min(limitY, newY));
        }
        setOffset({ x: newX, y: newY });
      } else if (state.isDrawing) {
        // --- 绘图 ---
        e.preventDefault();
        const { x, y } = getCanvasPoint(e.clientX, e.clientY);
        state.currentMousePos = { x, y };

        if (state.drawTool === "brush" || state.drawTool === "eraser") {
          drawLine(x, y, false);
        } else if (state.drawTool === "rectangle" || state.drawTool === "circle") {
          // 实时预览：清除画布并重绘
          if (canvasRef.current && tempCanvasRef.current) {
            const ctx = canvasRef.current.getContext("2d");

            // 恢复临时保存的画布
            ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
            ctx.drawImage(tempCanvasRef.current, 0, 0);

            // 绘制预览
            ctx.strokeStyle = maskColor;
            ctx.lineWidth = brushSize;
            ctx.globalAlpha = brushOpacity;
            ctx.globalCompositeOperation = "source-over";

            if (state.drawTool === "rectangle") {
              const width = x - state.drawStart.x;
              const height = y - state.drawStart.y;
              ctx.strokeRect(state.drawStart.x, state.drawStart.y, width, height);
            } else if (state.drawTool === "circle") {
              const radius = Math.sqrt(
                Math.pow(x - state.drawStart.x, 2) + Math.pow(y - state.drawStart.y, 2)
              );
              ctx.beginPath();
              ctx.arc(state.drawStart.x, state.drawStart.y, radius, 0, 2 * Math.PI);
              ctx.stroke();
            }
          }
        }
      }
    },
    [brushSize, maskColor, brushOpacity]
  );

  const handleWindowMouseUp = useCallback(() => {
    const state = stateRef.current;

    // 如果是矩形或圆形工具，完成绘制
    if (state.isDrawing && (state.drawTool === "rectangle" || state.drawTool === "circle")) {
      const currentPos = state.currentMousePos;
      if (currentPos && canvasRef.current && maskCanvasRef.current) {
        if (state.drawTool === "rectangle") {
          drawRectangle(state.drawStart.x, state.drawStart.y, currentPos.x, currentPos.y);
        } else if (state.drawTool === "circle") {
          const radius = Math.sqrt(
            Math.pow(currentPos.x - state.drawStart.x, 2) +
            Math.pow(currentPos.y - state.drawStart.y, 2)
          );
          drawCircle(state.drawStart.x, state.drawStart.y, radius);
        }
        saveToHistory();
      }
    } else if (state.isDrawing && (state.drawTool === "brush" || state.drawTool === "eraser")) {
      // 画笔和橡皮擦完成时保存历史
      saveToHistory();
    }

    stateRef.current.isDragging = false;
    stateRef.current.isDrawing = false;
    stateRef.current.currentMousePos = null;
    window.removeEventListener("mousemove", handleWindowMouseMove);
    window.removeEventListener("mouseup", handleWindowMouseUp);

    if (canvasRef.current) canvasRef.current.getContext("2d").closePath();
    if (maskCanvasRef.current)
      maskCanvasRef.current.getContext("2d").closePath();
  }, [handleWindowMouseMove, saveToHistory, drawRectangle, drawCircle]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    const state = stateRef.current;

    if (state.mode === "mask" && !state.isSpacePressed) {
      // 在开始绘制前，如果历史记录为空，先保存当前状态
      if (history.length === 0 && canvasRef.current && maskCanvasRef.current) {
        saveToHistory();
      }

      state.isDrawing = true;
      const { x, y } = getCanvasPoint(e.clientX, e.clientY);
      state.drawStart = { x, y };
      state.currentMousePos = { x, y };

      if (state.drawTool === "brush" || state.drawTool === "eraser") {
        drawLine(x, y, true);
      } else if (state.drawTool === "rectangle" || state.drawTool === "circle") {
        // 保存当前画布状态用于实时预览
        if (!tempCanvasRef.current) {
          tempCanvasRef.current = document.createElement("canvas");
        }
        if (canvasRef.current) {
          tempCanvasRef.current.width = canvasRef.current.width;
          tempCanvasRef.current.height = canvasRef.current.height;
          const tempCtx = tempCanvasRef.current.getContext("2d");
          tempCtx.drawImage(canvasRef.current, 0, 0);
        }
      }
    } else {
      state.isDragging = true;
      state.dragStart = {
        x: e.clientX - state.offset.x,
        y: e.clientY - state.offset.y,
      };
    }

    window.addEventListener("mousemove", handleWindowMouseMove);
    window.addEventListener("mouseup", handleWindowMouseUp);
  };

  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.target.tagName === "TEXTAREA" || e.target.tagName === "INPUT")
        return;

      // 撤销/重做快捷键
      if ((e.ctrlKey || e.metaKey) && e.key === 'z' && !e.shiftKey) {
        e.preventDefault();
        handleUndo();
      } else if ((e.ctrlKey || e.metaKey) && (e.key === 'y' || (e.key === 'z' && e.shiftKey))) {
        e.preventDefault();
        handleRedo();
      } else if (e.code === "Space" && !e.repeat) {
        e.preventDefault();
        setIsSpacePressed(true);
        stateRef.current.isSpacePressed = true;
      }
    };
    const handleKeyUp = (e) => {
      if (e.code === "Space") {
        e.preventDefault();
        setIsSpacePressed(false);
        stateRef.current.isSpacePressed = false;
        if (stateRef.current.isDragging) {
          handleWindowMouseUp();
        }
      }
    };
    window.addEventListener("keydown", handleKeyDown, { passive: false });
    window.addEventListener("keyup", handleKeyUp);
    return () => {
      window.removeEventListener("keydown", handleKeyDown);
      window.removeEventListener("keyup", handleKeyUp);
    };
  }, [handleWindowMouseUp, handleUndo, handleRedo]);

  // 使用非 passive 事件监听器处理滚轮缩放
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    const handleWheel = (e) => {
      e.preventDefault();
      const delta = e.deltaY > 0 ? 0.9 : 1.1;
      setScale(prev => Math.max(0.1, Math.min(prev * delta, 10)));
    };
    viewport.addEventListener('wheel', handleWheel, { passive: false });
    return () => viewport.removeEventListener('wheel', handleWheel);
  }, []);

  const clearMask = () => {
    if (canvasRef.current) {
      const ctx = canvasRef.current.getContext("2d");
      ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height);
    }
    if (maskCanvasRef.current) {
      const ctx = maskCanvasRef.current.getContext("2d");
      ctx.clearRect(
        0,
        0,
        maskCanvasRef.current.width,
        maskCanvasRef.current.height
      );
    }
    setHasMask(false);
    setHistory([]);
    setHistoryIndex(-1);
  };

  // ================= AI 功能 =================

  // 万相编辑提交
  const handleWanxSubmit = async () => {
    if (!wanxPrompt) return toast.warning("请输入提示词");
    if (!currentImage) return toast.warning("当前图片不可用，请重新加载后再试");

    const validReferenceImages = wanxRefImages.filter((ref) => ref?.url);
    if (validReferenceImages.length > WANX_MAX_EXTRA_REFERENCE_IMAGES) {
      notifyWanxReferenceLimit();
      return;
    }

    setWanxProcessing(true);
    try {
      const imageUrls = [currentImage, ...validReferenceImages.map((ref) => ref.url)];
      const taskPayload = {
        model: WANX_IMAGE_MODEL,
        prompt: wanxPrompt,
        imageUrls,
        n: 1,
        projectId: projectId ? parseInt(projectId) : null,
        episodeId: episodeId ? parseInt(episodeId) : null,
      };

      if (wanxSize !== "auto") {
        const displayedImage = imgRef.current;
        const isCurrentImageLoaded = displayedImage?.complete
          && displayedImage.getAttribute("src") === currentImage;
        let naturalWidth = isCurrentImageLoaded ? displayedImage.naturalWidth : 0;
        let naturalHeight = isCurrentImageLoaded ? displayedImage.naturalHeight : 0;
        if (!naturalWidth || !naturalHeight) {
          const naturalSize = await loadImageNaturalSize(currentImage);
          naturalWidth = naturalSize.width;
          naturalHeight = naturalSize.height;
        }
        taskPayload.size = toWanxOutputSize(naturalWidth, naturalHeight, wanxSize);
      }

      const { taskId } = await submitTask("WANX_IMAGE_EDIT", taskPayload, { provider: "wanx" });

      startTaskPolling(taskId, {
        onSuccess: (result) => {
          const newUrl = extractTaskResultUrl(result);
          if (!newUrl) {
            toast.error("千问图像3.0返回数据无效");
            setWanxProcessing(false);
            return;
          }
          pushImage(newUrl);
          setMode("view");
          clearMask();
          toast.success("千问图像3.0编辑完成");
          setWanxProcessing(false);
        },
        onError: (errorMessage) => {
          toast.error("千问图像3.0编辑失败: " + errorMessage);
          setWanxProcessing(false);
        },
      });
    } catch (e) {
      toast.error("提交失败: " + e.message);
      setWanxProcessing(false);
    }
  };

  // 1. 高级重绘 (发送彩色涂鸦)
  const handleRedraw = async () => {
    if (!prompt) return toast.warning("请输入重绘提示词");
    const imageProvider = getCurrentImageProvider();
    if (mode === "mask" && isWanxImageProvider(imageProvider)) {
      setMode("wanx");
      return toast.warning("千问图像3.0不支持蒙版框选，已切换到千问编辑，请直接用文字描述修改区域");
    }
    if (mode === "mask" && !hasMask) return toast.warning("请先涂抹需要修改的区域");

    setIsProcessing(true);
    try {
      let maskBase64 = null;

      if (mode === "mask") {
        if (maskCanvasRef.current) {
           maskBase64 = maskCanvasRef.current.toDataURL("image/png");
        }
      }

      const taskPayload = {
        baseImage: currentImage,
        maskImage: maskBase64,
        prompt: prompt,
      };

      const { taskId } = await submitTask("REDRAW_IMAGE", taskPayload, { provider: imageProvider });

      startTaskPolling(taskId, {
        onSuccess: (result) => {
          const newUrl = extractTaskResultUrl(result);
          if (!newUrl) {
            toast.error("AI重绘返回数据无效");
            setIsProcessing(false);
            return;
          }
          pushImage(newUrl);
          setMode("view");
          setHasMask(false);
          setAnalysisResult(null);
          clearMask();
          setIsProcessing(false);
        },
        onError: (errorMessage) => {
          toast.error("AI重绘失败: " + errorMessage);
          setIsProcessing(false);
        },
      });
    } catch (e) {
      toast.error("AI重绘提交失败: " + e.message);
      setIsProcessing(false);
    }
  };

  // 2. 找回：视觉分析 (JSON)
  const handleAnalyze = async () => {
    setIsProcessing(true);
    setShowJsonPanel(true);
    try {
      const { taskId } = await submitTask("ANALYZE_IMAGE", {
        imageInput: currentImage,
      });

      startTaskPolling(taskId, {
        onSuccess: (result) => {
          const parsed = typeof result === 'string' ? JSON.parse(result) : result;
          setAnalysisResult(parsed);
          setIsProcessing(false);
        },
        onError: (errorMessage) => {
          setAnalysisResult({ error: "无法识别图片信息", details: errorMessage });
          setIsProcessing(false);
        },
      });
    } catch (e) {
      setAnalysisResult({ error: "无法识别图片信息", details: e.message });
      setIsProcessing(false);
    }
  };

  const handleDownload = () => {
    const link = document.createElement("a");
    link.href = currentImage;
    link.download = `workstation_edit_${Date.now()}.png`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  return (
    <div
      className="fixed top-0 bottom-0 right-0 z-[60] flex flex-col animate-in fade-in duration-200 transition-[left] duration-500 ease-[cubic-bezier(0.25,0.1,0.25,1)]"
      style={{ left: leftOffset, backgroundColor: 'var(--color-bg-overlay)', backdropFilter: 'blur(12px)' }}
    >
      <div className="h-16 flex items-center justify-between px-6 flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-secondary)', borderBottom: 'var(--color-border-primary)' }}>
        <div className="flex items-center gap-4">
          <div className="flex items-center gap-2 font-bold text-lg" style={{ color: 'var(--color-text-primary)' }}>
            <Layers style={{ color: theme.colors.bluePrimary }} /> 图片工作站
          </div>
          <div className="w-px h-6 mx-2" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>
          <div className="flex rounded-lg p-1 border" style={{ backgroundColor: 'var(--color-bg-overlay)', borderColor: 'var(--color-border-primary)' }}>
            <button
              onClick={() => setMode("view")}
              className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-2 transition-all"
              style={{
                backgroundColor: mode === "view" ? theme.colors.bgTertiary : 'transparent',
                color: mode === "view" ? 'var(--color-text-primary)' : 'var(--color-text-muted)'
              }}
            >
              <Maximize2 size={14} /> 查看模式
            </button>
            <button
              onClick={() => setMode("mask")}
              className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-2 transition-all"
              style={{
                backgroundColor: mode === "mask" ? theme.colors.bluePrimary : 'transparent',
                color: mode === "mask" ? 'white' : 'var(--color-text-muted)'
              }}
            >
              <Brush size={14} /> 修补模式
            </button>
            <button
              onClick={() => setMode("wanx")}
              className="px-3 py-1.5 rounded-md text-xs font-bold flex items-center gap-2 transition-all"
              style={{
                backgroundColor: mode === "wanx" ? theme.colors.accentPrimary : 'transparent',
                color: mode === "wanx" ? 'white' : 'var(--color-text-muted)'
              }}
            >
              <Wand2 size={14} /> 千问编辑
            </button>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* 图片历史撤回/重做 */}
          <button
            onClick={undoImage}
            disabled={!canUndoImage}
            className="p-2 rounded-full transition-all duration-200 disabled:opacity-30"
            style={{ color: 'var(--color-text-muted)' }}
            title="撤回上一张图片"
          >
            <RotateCcw size={18} />
          </button>
          <button
            onClick={redoImage}
            disabled={!canRedoImage}
            className="p-2 rounded-full transition-all duration-200 disabled:opacity-30"
            style={{ color: 'var(--color-text-muted)' }}
            title="恢复下一张图片"
          >
            <Redo size={18} />
          </button>
          <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>
          <button
            onClick={handleDownload}
            className="p-2 rounded-full transition-all duration-200"
            style={{ color: 'var(--color-text-muted)' }}
            title="下载当前图片"
          >
            <Download size={20} />
          </button>
          <button
            onClick={() => onSave && onSave(currentImage)}
            className="px-4 py-2 rounded-lg text-sm font-bold flex items-center gap-2 shadow-lg"
            style={{ backgroundColor: theme.colors.greenPrimary, color: 'white' }}
          >
            <Save size={16} /> 保存并使用
          </button>
          <button
            onClick={onClose}
            className="p-2 rounded-full transition-all duration-200 ml-2"
            style={{ color: 'var(--color-text-muted)' }}
          >
            <X size={24} />
          </button>
        </div>
      </div>

      <div className="flex-1 flex overflow-hidden">
        <div
          ref={viewportRef}
          className="flex-1 relative bg-[url('https://res.cloudinary.com/dr0w02w5v/image/upload/v1705663673/transparent-bg_c80218.png')] bg-repeat overflow-hidden select-none"
          onMouseDown={handleMouseDown}
          style={{
            cursor: isSpacePressed || mode === "view"
              ? "grab"
              : mode === "wanx"
              ? "grab"
              : drawTool === "brush"
              ? "crosshair"
              : drawTool === "eraser"
              ? "not-allowed"
              : drawTool === "rectangle" || drawTool === "circle"
              ? "crosshair"
              : "default",
          }}
        >
          <div
            className="w-full h-full flex items-center justify-center transition-transform duration-75 ease-linear"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              pointerEvents: "none",
            }}
          >
            <div className="relative shadow-2xl pointer-events-auto">
              {/* ✅ 图片加载完成后，设置 Canvas 尺寸 */}
              <img
                ref={imgRef}
                src={currentImage}
                className="max-w-none pointer-events-none select-none block"
                alt="Workstation Target"
                onLoad={handleImageLoad}
              />
              <canvas
                ref={canvasRef}
                className={`absolute inset-0 z-10 ${
                  mode === "mask" ? "opacity-100" : "opacity-50"
                }`}
              />
            </div>
          </div>

          {mode === "mask" && (
            <div
              className="absolute bottom-6 left-1/2 -translate-x-1/2 rounded-full px-6 py-3 flex items-center gap-6 shadow-2xl z-20 pointer-events-auto"
              onMouseDown={(e) => e.stopPropagation()}
              style={{ backgroundColor: 'var(--color-bg-secondary)', backdropFilter: 'blur(12px)', border: '1px solid var(--color-border-primary)' }}
            >
              {/* 工具选择 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={() => setDrawTool("brush")}
                  className={`p-2 rounded transition-all ${drawTool === "brush" ? "shadow-lg" : ""}`}
                  style={{
                    backgroundColor: drawTool === "brush" ? theme.colors.bluePrimary : 'transparent',
                    color: drawTool === "brush" ? 'white' : 'var(--color-text-muted)'
                  }}
                  title="画笔"
                >
                  <Brush size={16} />
                </button>
                <button
                  onClick={() => setDrawTool("rectangle")}
                  className={`p-2 rounded transition-all ${drawTool === "rectangle" ? "shadow-lg" : ""}`}
                  style={{
                    backgroundColor: drawTool === "rectangle" ? theme.colors.bluePrimary : 'transparent',
                    color: drawTool === "rectangle" ? 'white' : 'var(--color-text-muted)'
                  }}
                  title="矩形框"
                >
                  <Square size={16} />
                </button>
                <button
                  onClick={() => setDrawTool("circle")}
                  className={`p-2 rounded transition-all ${drawTool === "circle" ? "shadow-lg" : ""}`}
                  style={{
                    backgroundColor: drawTool === "circle" ? theme.colors.bluePrimary : 'transparent',
                    color: drawTool === "circle" ? 'white' : 'var(--color-text-muted)'
                  }}
                  title="圆形"
                >
                  <Circle size={16} />
                </button>
                <button
                  onClick={() => setDrawTool("eraser")}
                  className={`p-2 rounded transition-all ${drawTool === "eraser" ? "shadow-lg" : ""}`}
                  style={{
                    backgroundColor: drawTool === "eraser" ? theme.colors.redPrimary : 'transparent',
                    color: drawTool === "eraser" ? 'white' : 'var(--color-text-muted)'
                  }}
                  title="橡皮擦"
                >
                  <Eraser size={16} />
                </button>
              </div>

              <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

              {/* 画笔大小 */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>大小</span>
                <input
                  type="range"
                  min="5"
                  max="100"
                  value={brushSize}
                  onChange={(e) => setBrushSize(Number(e.target.value))}
                  className="w-24"
                  style={{ accentColor: theme.colors.bluePrimary }}
                />
                <div
                  className="w-6 h-6 rounded-full border"
                  style={{
                    backgroundColor: drawTool === "eraser" ? "var(--color-text-muted)" : maskColor,
                    transform: `scale(${brushSize / 40})`,
                    borderColor: 'var(--color-border-primary)'
                  }}
                ></div>
              </div>

              <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

              {/* 透明度 */}
              <div className="flex items-center gap-3">
                <span className="text-xs font-bold" style={{ color: 'var(--color-text-secondary)' }}>透明度</span>
                <input
                  type="range"
                  min="0.1"
                  max="1"
                  step="0.1"
                  value={brushOpacity}
                  onChange={(e) => setBrushOpacity(Number(e.target.value))}
                  className="w-20"
                  style={{ accentColor: theme.colors.bluePrimary }}
                />
                <span className="text-xs font-mono" style={{ color: 'var(--color-text-muted)', minWidth: '30px' }}>
                  {Math.round(brushOpacity * 100)}%
                </span>
              </div>

              <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

              {/* 颜色选择 */}
              {drawTool !== "eraser" && (
                <>
                  <div className="flex items-center gap-2 relative group">
                    <Palette size={14} style={{ color: 'var(--color-text-muted)' }} />
                    <div className="w-6 h-6 rounded-full overflow-hidden border relative cursor-pointer" style={{ borderColor: 'var(--color-border-primary)' }}>
                      <input
                        type="color"
                        value={maskColor}
                        onChange={(e) => setMaskColor(e.target.value)}
                        className="absolute inset-0 w-[150%] h-[150%] -top-1/4 -left-1/4 p-0 border-0 cursor-pointer"
                      />
                    </div>
                  </div>
                  <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>
                </>
              )}

              {/* 撤销/重做 */}
              <div className="flex items-center gap-2">
                <button
                  onClick={handleUndo}
                  disabled={historyIndex <= 0}
                  className="p-2 rounded-lg transition-all duration-200 disabled:opacity-30"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="撤销 (Ctrl+Z)"
                >
                  <Undo size={16} />
                </button>
                <button
                  onClick={handleRedo}
                  disabled={historyIndex >= history.length - 1}
                  className="p-2 rounded-lg transition-all duration-200 disabled:opacity-30"
                  style={{ color: 'var(--color-text-muted)' }}
                  title="重做 (Ctrl+Y)"
                >
                  <Redo size={16} />
                </button>
              </div>

              <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

              {/* 重置按钮 */}
              <button
                onClick={clearMask}
                className="flex items-center gap-2 text-xs font-bold transition-all duration-200"
                style={{ color: 'var(--color-text-muted)' }}
              >
                <RotateCcw size={14} /> 清空
              </button>

              <div className="w-px h-6" style={{ backgroundColor: 'var(--color-border-primary)' }}></div>

              {/* 提示 */}
              <div className="flex items-center gap-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                <Hand size={12} /> 按住空格平移
              </div>
            </div>
          )}
          <div className="absolute bottom-6 left-6 px-3 py-1.5 rounded text-xs font-mono pointer-events-none border" style={{ backgroundColor: 'var(--color-bg-overlay)', backdropFilter: 'blur(8px)', color: 'var(--color-text-muted)', borderColor: 'var(--color-border-primary)' }}>
            {(scale * 100).toFixed(0)}%
          </div>
        </div>

        <div className="w-96 flex flex-col z-20 shadow-2xl flex-shrink-0" style={{ backgroundColor: 'var(--color-bg-secondary)', borderLeft: '1px solid var(--color-border-primary)' }}>
          <div className="p-6 flex-1 overflow-y-auto custom-scrollbar space-y-8">
            {mode === "wanx" ? (
              <WanxEditPanel
                feature={wanxFeature}
                onFeatureChange={setWanxFeature}
                prompt={wanxPrompt}
                onPromptChange={setWanxPrompt}
                refImages={wanxRefImages}
                onAddRefImage={addWanxReferenceImage}
                onRemoveRefImage={(idx) => setWanxRefImages(prev => prev.filter((_, i) => i !== idx))}
                onReferenceLimitReached={notifyWanxReferenceLimit}
                size={wanxSize}
                onSizeChange={setWanxSize}
                isProcessing={wanxProcessing}
                onSubmit={handleWanxSubmit}
                onOpenResourceSelector={openWanxResourceSelector}
                theme={theme}
              />
            ) : (
              <>
            <div className="space-y-4">
              <h3 className="text-sm font-bold flex items-center gap-2 uppercase tracking-wider" style={{ color: 'var(--color-text-primary)' }}>
                <Wand2 size={16} style={{ color: theme.colors.bluePrimary }} /> 智能重绘
              </h3>
              <div className="rounded-xl p-4 border space-y-3" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}>
                <p className="text-xs leading-relaxed" style={{ color: 'var(--color-text-muted)' }}>
                  {mode === "mask"
                    ? "已进入修补模式。使用画笔涂抹、矩形框选或圆形圈选需要修改的区域，然后输入提示词。支持多种颜色标记不同区域。"
                    : "当前为全图模式。输入提示词将对整张图片进行风格化或调整。"}
                </p>
                <div className="flex flex-wrap gap-2 text-2xs" style={{ color: 'var(--color-text-muted)' }}>
                  <span className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Brush size={10} /> 画笔涂抹</span>
                  <span className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Square size={10} /> 矩形框选</span>
                  <span className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Circle size={10} /> 圆形圈选</span>
                  <span className="px-2 py-1 rounded inline-flex items-center gap-1" style={{ backgroundColor: 'var(--color-bg-tertiary)' }}><Eraser size={10} /> 橡皮擦除</span>
                </div>
                <textarea
                  value={prompt}
                  onChange={(e) => setPrompt(e.target.value)}
                  placeholder={
                    mode === "mask"
                      ? "例如：删除红线框选的内容，把绿圈里的人换成猫，将蓝色区域改成森林..."
                      : "例如：赛博朋克风格，霓虹灯光..."
                  }
                  className="w-full h-24 rounded-lg p-3 text-sm outline-none resize-none"
                  style={{
                    backgroundColor: 'var(--color-bg-overlay)',
                    border: '1px solid var(--color-border-primary)',
                    color: 'var(--color-text-primary)'
                  }}
                />
                <button
                  onClick={handleRedraw}
                  disabled={isProcessing}
                  className="w-full py-2.5 rounded-lg text-xs font-bold flex items-center justify-center gap-2 shadow-lg disabled:opacity-50 transition-all"
                  style={{
                    background: 'var(--color-accent-primary)',
                    color: 'white'
                  }}
                >
                  {isProcessing ? (
                    <Loader2 className="animate-spin" size={14} />
                  ) : (
                    <Wand2 size={14} />
                  )}
                  {mode === "mask" ? "生成局部修补" : "全图重绘"}
                </button>
              </div>
            </div>
            <div className="space-y-4">
              <h3 className="text-sm font-bold flex items-center gap-2 uppercase tracking-wider" style={{ color: 'var(--color-text-primary)' }}>
                <ScanEye size={16} style={{ color: theme.colors.bluePrimary }} /> 视觉分析
              </h3>
              {!showJsonPanel ? (
                <button
                  onClick={handleAnalyze}
                  disabled={isProcessing}
                  className="w-full py-3 rounded-xl text-xs font-bold transition-all flex items-center justify-center gap-2 border"
                  style={{
                    borderStyle: 'dashed',
                    borderColor: 'var(--color-border-primary)',
                    color: 'var(--color-text-muted)'
                  }}
                >
                  <Code size={14} /> 读取图片信息 (JSON)
                </button>
              ) : (
                <div className="rounded-xl border overflow-hidden flex flex-col h-64" style={{ backgroundColor: 'var(--color-bg-primary)', borderColor: 'var(--color-border-primary)' }}>
                  <div className="px-3 py-2 flex items-center justify-between border-b" style={{ backgroundColor: 'var(--color-bg-tertiary)', borderColor: 'var(--color-border-primary)' }}>
                    <span className="text-2xs font-mono" style={{ color: 'var(--color-text-secondary)' }}>
                      ANALYSIS_RESULT.JSON
                    </span>
                    <button
                      onClick={() => setShowJsonPanel(false)}
                      style={{ color: 'var(--color-text-muted)' }}
                    >
                      <X size={12} />
                    </button>
                  </div>
                  <div className="flex-1 overflow-auto p-3 custom-scrollbar relative">
                    {isProcessing && !analysisResult ? (
                      <div className="absolute inset-0 flex items-center justify-center flex-col gap-2" style={{ color: theme.colors.bluePrimary }}>
                        <Loader2 className="animate-spin" size={24} />
                        <span className="text-xs font-bold animate-pulse">
                          正在分析像素...
                        </span>
                      </div>
                    ) : (
                      <pre className="text-2xs font-mono leading-relaxed whitespace-pre-wrap" style={{ color: 'var(--color-success)' }}>
                        {JSON.stringify(analysisResult, null, 2)}
                      </pre>
                    )}
                  </div>
                  {analysisResult && (
                    <div className="p-2" style={{ borderTop: '1px solid var(--color-border-secondary)', backgroundColor: 'var(--color-bg-tertiary)' }}>
                      <button
                        onClick={() =>
                          navigator.clipboard.writeText(
                            JSON.stringify(analysisResult, null, 2)
                          )
                        }
                        className="w-full py-1.5 rounded text-2xs font-bold transition-all duration-200"
                        style={{ backgroundColor: 'var(--color-bg-secondary)', color: 'var(--color-text-secondary)' }}
                      >
                        复制 JSON
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
              </>
            )}
          </div>
        </div>
      </div>

      {/* ResourceSelector for wanx mode */}
      <ResourceSelector
        isOpen={showResourceSelector}
        onClose={() => setShowResourceSelector(false)}
        onSelect={(resource) => {
          if (resource && resource.url) {
            addWanxReferenceImage({ url: resource.url, name: resource.name || "资源图片" });
          }
          setShowResourceSelector(false);
        }}
        projectId={projectId}
        type="image"
        title="选择参考图片"
      />
    </div>
  );
};

export default ImageWorkstation;
